"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Search, PenTool, Activity, HardHat, ShieldCheck, ChevronRight, FileText, CircleCheck } from "lucide-react";
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
const steps = [{
  id: "01",
  title: "Sourcing",
  description: "Selecting the finest organic grains and local seasonal ingredients.",
  icon: Search
}, {
  id: "02",
  title: "Mixing",
  description: "Slow hydration and natural leavening for depth of flavor.",
  icon: PenTool
}, {
  id: "03",
  title: "Proofing",
  description: "Patient, temperature-controlled rising to develop texture.",
  icon: Activity
}, {
  id: "04",
  title: "Baking",
  description: "High-heat stone hearth baking for the perfect golden crust.",
  icon: HardHat
}, {
  id: "05",
  title: "Finishing",
  description: "Hand-applied glazes and delicate artistic decorations.",
  icon: ShieldCheck
}];
const subSteps = [{
  stepId: "01",
  text: "Stone-milled heirloom wheat selection"
}, {
  stepId: "01",
  text: "Local farm-to-bakery dairy sourcing"
}, {
  stepId: "01",
  text: "Seasonal fruit and nut quality check"
}, {
  stepId: "02",
  text: "Autolyse process for gluten development"
}, {
  stepId: "02",
  text: "Wild yeast starter integration"
}, {
  stepId: "02",
  text: "Small-batch hand-folding techniques"
}, {
  stepId: "03",
  text: "24-hour cold fermentation cycle"
}, {
  stepId: "03",
  text: "Humidity-monitored proofing baskets"
}, {
  stepId: "03",
  text: "Dough elasticity and gas retention check"
}, {
  stepId: "04",
  text: "Artisanal scoring for steam release"
}, {
  stepId: "04",
  text: "Stone hearth temperature calibration"
}, {
  stepId: "04",
  text: "Maillard reaction monitoring for color"
}, {
  stepId: "05",
  text: "Natural honey and fruit glaze application"
}, {
  stepId: "05",
  text: "Hand-piped botanical decorations"
}, {
  stepId: "05",
  text: "Final quality and crumb structure audit"
}];
const deliverables = [{
  stepId: "01",
  text: "Ingredient Provenance Log"
}, {
  stepId: "01",
  text: "Seasonal Flavor Profile"
}, {
  stepId: "02",
  text: "Hydration Ratio Records"
}, {
  stepId: "02",
  text: "Starter Vitality Report"
}, {
  stepId: "03",
  text: "Fermentation Timeline"
}, {
  stepId: "03",
  text: "Texture Consistency Check"
}, {
  stepId: "04",
  text: "Crust Thickness Analysis"
}, {
  stepId: "04",
  text: "Internal Temperature Log"
}, {
  stepId: "05",
  text: "Visual Presentation Standard"
}, {
  stepId: "05",
  text: "Freshness Guarantee Stamp"
}];
export default function ConsultingWorkflow() {
  return <section data-section-id="224948" id="the-artisan-process" className="relative w-full bg-background py-24 overflow-hidden border-y border-border dark">
      {}
      <div className="absolute inset-0 z-0 opacity-[0.05] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1645038/img/grain_texture.png)] bg-repeat mix-blend-overlay" />
      
      {}
      <div className="absolute inset-0 z-0 bg-[linear-gradient(to_right,#965A3808_1px,transparent_1px),linear-gradient(to_bottom,#965A3808_1px,transparent_1px)] bg-[size:32px_32px]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-6 border border-primary/30 bg-primary/10 text-primary text-xs font-mono tracking-widest uppercase">
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            From Grain to Crust
          </div>
          <h2 className="text-3xl md:text-5xl font-bold font-default text-foreground mb-4 tracking-tight">
            The Artisan Process
          </h2>
          <p className="text-lg text-muted-foreground font-default max-w-2xl">
            A patient, time-honored approach to boutique baking. From the selection of heirloom grains to the final golden glaze, our process ensures every bite delivers comforting nostalgia and premium quality.
          </p>
        </div>

        {}
        <div className="relative w-full overflow-x-auto pb-8 -mx-4 px-4 md:mx-0 md:px-0 hide-scrollbar">
          <div className="min-w-[800px] md:min-w-full relative pt-12 pb-4">
            
            {}
            <div className="absolute top-[4.5rem] left-8 right-8 h-[1px] bg-border">
              <div className="absolute inset-0 bg-primary/30 shadow-sm" />
            </div>

            <div className="flex justify-between relative z-10">
              {steps.map((step, i) => {
              const Icon = step.icon;
              const stepSubSteps = subSteps.filter(s => s.stepId === step.id);
              const stepDeliverables = deliverables.filter(d => d.stepId === step.id);
              return <SafeHydrate><Drawer key={step.id}>
                    <DrawerTrigger asChild data-index={i}>
                      <div className="flex flex-col items-center group cursor-pointer w-48 shrink-0">
                        {}
                        <div className="relative w-16 h-16 rounded-full border border-border bg-card flex items-center justify-center text-muted-foreground transition-all duration-500 group-hover:border-primary group-hover:text-primary group-hover:shadow-xl group-hover:-translate-y-2 mb-6">
                          <div className="flex items-center justify-center">
                            <Icon className="w-6 h-6" />
                          </div>
                          {}
                          <div className="absolute -top-1 -left-1 w-3 h-3 border-t border-l border-primary/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-tl-full" />
                          <div className="absolute -bottom-1 -right-1 w-3 h-3 border-b border-r border-primary/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-br-full" />
                        </div>
                        
                        {}
                        <div className="text-center">
                          <span className="block text-xs font-mono text-primary mb-2 opacity-80 tracking-widest">
                            STAGE_{step.id}
                          </span>
                          <h3 className="text-lg font-bold font-default text-foreground group-hover:text-primary transition-colors">
                            {step.title}
                          </h3>
                        </div>
                      </div>
                    </DrawerTrigger>
                    
                    <DrawerContent className="border-t border-border bg-background/98 backdrop-blur-2xl">
                      <div className="mx-auto w-full max-w-4xl">
                        <DrawerHeader className="pb-8 border-b border-border/50">
                          <div className="flex items-center gap-4 mb-2">
                            <span className="flex items-center justify-center w-12 h-12 bg-primary/10 text-primary font-mono text-lg border border-primary/20 rounded-full">
                              {step.id}
                            </span>
                            <div>
                              <DrawerTitle className="text-2xl md:text-3xl font-default text-foreground">
                                {step.title} Stage
                              </DrawerTitle>
                              <DrawerDescription className="text-base text-muted-foreground mt-1 font-default">
                                {step.description}
                              </DrawerDescription>
                            </div>
                          </div>
                        </DrawerHeader>
                        
                        <div className="p-4 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                          {}
                          <div className="space-y-4">
                            <h4 className="text-sm font-mono text-foreground uppercase tracking-widest flex items-center gap-2 border-b border-border pb-2">
                              <Activity className="w-4 h-4 text-primary" />
                              Craft Protocol
                            </h4>
                            <ul className="space-y-3">
                              {stepSubSteps.map((subStep, j) => <li key={j} data-index={j} className="flex items-start gap-3 text-sm font-default text-muted-foreground">
                                  <ChevronRight className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                  <span>{subStep.text}</span>
                                </li>)}
                            </ul>
                          </div>

                          {}
                          <div className="space-y-4">
                            <h4 className="text-sm font-mono text-foreground uppercase tracking-widest flex items-center gap-2 border-b border-border pb-2">
                              <FileText className="w-4 h-4 text-primary" />
                              Quality Markers
                            </h4>
                            <ul className="space-y-3">
                              {stepDeliverables.map((deliverable, k) => <li key={k} data-index={k} className="flex items-start gap-3 text-sm font-default text-muted-foreground bg-muted/20 p-3 border border-border/50 rounded-md">
                                  <CircleCheck className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                  <span>{deliverable.text}</span>
                                </li>)}
                            </ul>
                          </div>
                        </div>

                        <DrawerFooter className="border-t border-border/50 pt-6 pb-8 sm:justify-between sm:flex-row">
                          <p className="text-xs font-mono text-muted-foreground hidden sm:block">
                            BATCH_STATUS: OPTIMAL_RISE
                          </p>
                          <DrawerClose asChild>
                            <Button variant="outline" className="w-full sm:w-auto font-mono uppercase tracking-widest text-xs border-primary/20 hover:bg-primary/5">
                              Return to Bakery
                            </Button>
                          </DrawerClose>
                        </DrawerFooter>
                      </div>
                    </DrawerContent>
                  </Drawer></SafeHydrate>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}